import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";

// Types matching apps/web/data/v2/models/series.ts — reconcile to single
// source once the API branch merges.
export interface ShowsSeries {
  id: string;
  created_at: string;
  name: string;
  venue_page_id: string;
}

export interface SeriesShowInfo {
  show_id: string;
  title: string | null;
  datetime: string | null;
  tixologi_event_id: string | null;
  start_date: string | null;
  end_date: string | null;
  time_zone: string | null;
  ticket_types_count: number;
}

export interface ShowsSeriesWithShows extends ShowsSeries {
  venue_page_show_ids: string[];
  shows: SeriesShowInfo[];
  unique_ticket_types_count: number;
}

export interface CreateSeriesRequest {
  name: string;
  venue_page_id: string;
  venue_page_show_ids: string[];
}

interface ApiResponse<T> {
  data: T;
}

export const SERIES_QUERY_KEY = "venuePageSeries";

/**
 * Fetch all series for a venue page
 */
export async function getSeriesForVenuePage(
  venuePageId: string
): Promise<ShowsSeriesWithShows[]> {
  try {
    const response = await getApiClient().get<
      ApiResponse<ShowsSeriesWithShows[]>
    >(`/api/v2/manager/shows/series?venue_page_id=${venuePageId}`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId },
      tags: { source: "getSeriesForVenuePage" },
    });
    throw error;
  }
}

/**
 * Create a new series and assign shows to it
 */
export async function createSeries(
  data: CreateSeriesRequest
): Promise<ShowsSeries> {
  try {
    const response = await getApiClient().post<ApiResponse<ShowsSeries>>(
      "/api/v2/manager/shows/series",
      data
    );
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { data },
      tags: { source: "createSeries" },
    });
    throw error;
  }
}

/**
 * Delete a series by ID
 */
export async function deleteSeries(seriesId: string): Promise<void> {
  try {
    await getApiClient().del(`/api/v2/manager/shows/series/${seriesId}`);
  } catch (error) {
    logException(error, {
      extra: { seriesId },
      tags: { source: "deleteSeries" },
    });
    throw error;
  }
}

/**
 * Unassign a show from a series. The show row itself is not deleted.
 */
export async function removeShowFromSeries(
  seriesId: string,
  showId: string
): Promise<void> {
  try {
    await getApiClient().del(
      `/api/v2/manager/shows/series/${seriesId}/shows/${showId}`
    );
  } catch (error) {
    logException(error, {
      extra: { seriesId, showId },
      tags: { source: "removeShowFromSeries" },
    });
    throw error;
  }
}

/**
 * Assign an existing show to an existing series. Idempotent — re-attaching a
 * show that's already in the series returns 204.
 */
export async function addShowToSeries(
  seriesId: string,
  showId: string
): Promise<void> {
  try {
    await getApiClient().put(
      `/api/v2/manager/shows/series/${seriesId}/shows/${showId}`
    );
  } catch (error) {
    logException(error, {
      extra: { seriesId, showId },
      tags: { source: "addShowToSeries" },
    });
    throw error;
  }
}

/**
 * Batch-assign a set of existing shows to a series in a single atomic update.
 */
export async function addShowsToSeries(
  seriesId: string,
  showIds: string[]
): Promise<void> {
  try {
    await getApiClient().post(
      `/api/v2/manager/shows/series/${seriesId}/shows`,
      { show_ids: showIds }
    );
  } catch (error) {
    logException(error, {
      extra: { seriesId, showIds },
      tags: { source: "addShowsToSeries" },
    });
    throw error;
  }
}
